import { useCallback, useEffect, useState } from 'react';
import apiClient from '@/lib/api/client';
import { getEmployees, assignEmployeeToJob, type Employee } from '@/lib/api/employees';

interface TeamMember {
  id: number;
  user_id: number;
  role?: string;
  assigned_at?: string;
  username?: string;
  employee_name?: string;
  effective_rate?: number;
}

// Job-level crew attribution (job_team_assignments) — the counterpart of the
// employee-side "Jobs" tab. Assigned workers see this job (schedule, documents,
// timesheet dropdown) in their portal.
export function JobCrewPanel({ jobId }: { jobId: string | number }) {
  const [team, setTeam] = useState<TeamMember[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [pick, setPick] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await apiClient.get(`/jobs/${jobId}/team`);
      setTeam(data.data || []);
      setError('');
    } catch {
      setError('Failed to load crew');
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    getEmployees().then(e => setEmployees(e.filter(x => x.status === 'active'))).catch(() => {});
  }, []);

  const assignedUserIds = new Set(team.map(t => t.user_id));
  const addable = employees.filter(e => !e.user_id || !assignedUserIds.has(Number(e.user_id)));

  const add = async () => {
    if (!pick) return;
    setBusy(true);
    try {
      await assignEmployeeToJob(pick, Number(jobId));
      setPick('');
      await load();
    } catch {
      setError('Failed to assign worker');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (userId: number, name: string) => {
    if (!window.confirm(`Remove ${name} from this job? They'll stop seeing it in their portal.`)) return;
    setBusy(true);
    try {
      await apiClient.delete(`/jobs/${jobId}/team/${userId}`);
      await load();
    } catch {
      setError('Failed to remove worker');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-start gap-2 bg-blue-50 border border-blue-100 rounded-lg px-3 py-2.5 text-xs text-blue-800">
        <span className="material-symbols-outlined text-base">info</span>
        Workers assigned here see this job in their portal — its schedule, worker-visible documents,
        and the job in their timesheet dropdown.
      </div>

      {error && <p className="text-sm text-red-600">{error}</p>}

      {/* Add */}
      <div className="flex items-end gap-2 flex-wrap">
        <label className="flex flex-col gap-1 flex-1 min-w-[14rem]">
          <span className="text-xs font-medium text-gray-500">Assign a worker</span>
          <select value={pick} onChange={e => setPick(e.target.value)}
            className="h-9 px-2 rounded-lg border border-gray-300 text-sm bg-white">
            <option value="">Choose a worker…</option>
            {addable.map(e => (
              <option key={e.id} value={e.id}>{e.name}{e.role ? ` — ${e.role}` : ''}</option>
            ))}
          </select>
        </label>
        <button onClick={add} disabled={!pick || busy}
          className="inline-flex items-center gap-1.5 h-9 px-4 rounded-lg bg-primary text-white text-sm font-semibold hover:opacity-90 disabled:opacity-50">
          <span className={`material-symbols-outlined text-base ${busy ? 'animate-spin' : ''}`}>{busy ? 'progress_activity' : 'person_add'}</span>
          Assign
        </button>
      </div>

      {/* Current crew */}
      {loading ? (
        <div className="h-16 animate-pulse bg-gray-100 rounded-xl" />
      ) : team.length === 0 ? (
        <p className="text-sm text-gray-500">No workers assigned to this job yet.</p>
      ) : (
        <div className="space-y-2">
          {team.map(m => {
            const name = m.employee_name || m.username || `User ${m.user_id}`;
            return (
              <div key={m.id} className="flex items-center gap-3 border border-gray-100 rounded-xl p-3">
                <div className="bg-primary/10 text-primary rounded-full h-9 w-9 flex items-center justify-center flex-shrink-0 font-bold">
                  {name.charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm text-gray-900 truncate">{name}</p>
                  <p className="text-xs text-gray-400">
                    {m.role || 'crew member'}
                    {m.effective_rate ? ` · $${Number(m.effective_rate).toFixed(2)}/h` : ''}
                    {m.assigned_at ? ` · since ${new Date(m.assigned_at).toLocaleDateString('en-CA', { month: 'short', day: 'numeric' })}` : ''}
                  </p>
                </div>
                <button onClick={() => remove(m.user_id, name)} disabled={busy}
                  className="text-gray-400 hover:text-red-600 disabled:opacity-40" title="Remove from job">
                  <span className="material-symbols-outlined text-base">close</span>
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
